import React, { useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { useNavigate } from 'react-router-dom';
import { Download, Loader, File } from 'lucide-react';
import { Button } from '../components/ui/button';
import Sidebar from '../components/Sidebar'; // Importa el Sidebar
import Topbar from '../components/Topbar';
import * as XLSX from 'xlsx';

const excelSerialToDate = (serial) => {
  const excelEpoch = new Date(1900, 0, 1); // Fecha base de Excel (1 de enero de 1900)
  const days = serial - 1; // Restar 1 porque Excel considera el 1 de enero de 1900 como día 1
  const milliseconds = days * 24 * 60 * 60 * 1000; // Convertir días a milisegundos
  const date = new Date(excelEpoch.getTime() + milliseconds); // Crear la fecha
  return date.toISOString().split('T')[0]; // Devolver solo la fecha en formato YYYY-MM-DD
};

const excelSerialToTime = (serial) => {
  const hours = Math.floor(serial * 24);
  const minutes = Math.floor((serial * 24 - hours) * 60);
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
};

// Utility functions
const removeAccents = (text) => {
  if (!text) return text;
  return text.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
};

// Add new function to sanitize product codes
const sanitizeProductCode = (code) => {
  if (!code) return code;
  // First replace special characters (except "-") with "-"
  let sanitized = code.replace(/[^\w\-]/g, '-');
  
  // Then remove any trailing non-alphanumeric characters at the end
  sanitized = sanitized.replace(/[-_\W]+$/, '');
  
  return sanitized;
};

const MassMovements = () => {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [activeItem, setActiveItem] = useState('Movimientos Masivos');
  const [file1, setFile1] = useState(null);
  const [xmlFiles, setXmlFiles] = useState([]);
  const [loading1, setLoading1] = useState(false);
  const [loadingXml, setLoadingXml] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [processedData, setProcessedData] = useState(null);
  const [processedXmlData, setProcessedXmlData] = useState(null);
  const [productsCache, setProductsCache] = useState({});
  const [previewData, setPreviewData] = useState(null);
  const [xmlPreviewData, setXmlPreviewData] = useState(null);
  const [searchValue, setSearchValue] = useState('');
  const [xmlDebugView, setXmlDebugView] = useState(false);
  const [previewProcessedXmlData, setPreviewProcessedXmlData] = useState(null);
  const navigate = useNavigate();

  const handleLogout = () => {
    console.log('Logout clicked!');
    localStorage.removeItem('token');
    localStorage.removeItem('role');
    setTimeout(() => {
      navigate('/login');
    }, 0);
  };

  const fetchProducts = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('No se ha encontrado un token de autenticación. Por favor inicie sesión nuevamente.');
      }
      
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/v1/productos`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (!response.ok) throw new Error('Error fetching products');
      const products = await response.json();
      
      const productLookup = {};
      products.forEach(product => {
        if (product.codigo_producto) {
          productLookup[product.codigo_producto.trim()] = product.id;
        }
      });
      
      setProductsCache(productLookup);
      return productLookup;
    } catch (error) {
      console.error('Error cargando productos:', error);
      setMessage({ type: 'error', text: 'Error cargando catálogo de productos: ' + error.message });
      return {};
    }
  };

  const transformData = async (jsonData) => {
    const token = localStorage.getItem('token');
    if (!token) {
      throw new Error('No se ha encontrado un token de autenticación. Por favor inicie sesión nuevamente.');
    }

    const [ubicaciones, bodegas, cuentas] = await Promise.all([
      fetch(`${process.env.REACT_APP_API_URL}/api/v1/clients/`, {
        headers: { 'Authorization': `Bearer ${token}` }
      }).then(res => res.json()),
      fetch(`${process.env.REACT_APP_API_URL}/api/v1/bodegas`, {
        headers: { 'Authorization': `Bearer ${token}` }
      }).then(res => res.json()),
      fetch(`${process.env.REACT_APP_API_URL}/api/v1/cuentas`, {
        headers: { 'Authorization': `Bearer ${token}` }
      }).then(res => res.json())
    ]);

    const ubicacionesMap = ubicaciones.reduce((acc, ubi) => {
      if (!acc[ubi.customer]) {
        acc[ubi.customer] = [];
      }
      acc[ubi.customer].push({
        City: ubi.City,
        State: ubi.State,
        address: ubi.address
      });
      return acc;
    }, {});

    const bodegasSet = new Set(bodegas.map(b => b.nombre));
    const cuentasSet = new Set(cuentas.map(c => c.nombre));

    const errors = [];
    jsonData.forEach((row, index) => {
      const rowNum = index + 2;
      
      if (!ubicacionesMap[row.customer]) {
        errors.push(`Fila ${rowNum}: Cliente "${row.customer}" no encontrado en el catálogo`);
      } else {
        const customerLocations = ubicacionesMap[row.customer];
        const locationMatch = customerLocations.find(loc => 
          loc.City === row.City && 
          loc.State === row.State && 
          loc.address === row.address
        );
        
        if (!locationMatch) {
          errors.push(`Fila ${rowNum}: La combinación de ciudad, estado y dirección no está registrada para el cliente "${row.customer}"`);
        }
      }

      if (!bodegasSet.has(row.Origin)) {
        errors.push(`Fila ${rowNum}: Origen "${row.Origin}" no encontrado en el catálogo de bodegas`);
      }

      if (!cuentasSet.has(row.cuenta)) {
        errors.push(`Fila ${rowNum}: Cuenta "${row.cuenta}" no encontrada en el catálogo`);
      }
    });

    if (errors.length > 0) {
      throw new Error('Errores de validación:\n' + errors.join('\n'));
    }

    const productLookup = Object.keys(productsCache).length > 0 ? 
      productsCache : 
      await fetchProducts();

    const invalidProducts = [];
    jsonData.forEach(item => {
      const codigoProducto = item.codigo_producto?.trim();
      if (!codigoProducto || !productLookup[codigoProducto]) {
        invalidProducts.push(codigoProducto || 'Código vacío');
      }
    });

    if (invalidProducts.length > 0) {
      throw new Error(`Los siguientes códigos de producto no fueron encontrados: ${invalidProducts.join(', ')}`);
    }

    const orderGroups = jsonData.reduce((groups, item) => {
      const key = item.Order_number;
      if (!groups[key]) {
        groups[key] = [];
      }
      groups[key].push(item);
      return groups;
    }, {});

    const transformedData = Object.entries(orderGroups).map(([orderNumber, items]) => {
      const firstItem = items[0];
      const appointmentDate = excelSerialToDate(firstItem.Appointment);
      const appointmentTime = excelSerialToTime(firstItem.Appointment_hr);

      const productos = items.map(item => ({
        product_id: productLookup[item.codigo_producto.trim()],
        product_qt: parseInt(item.product_qt)
      }));

      return {
        Customer_PO: String(firstItem.Customer_PO),
        Order_number: String(firstItem.Order_number),
        customer: firstItem.customer,
        City: firstItem.City,
        State: firstItem.State,
        address: firstItem.address || null,
        Appointment: appointmentDate,
        Appointment_hr: appointmentTime,
        Origin: firstItem.Origin,
        cuenta: firstItem.cuenta,
        productos: productos,
        Order_status: "CREATED",
        order_Substatus: "PENDIENTE"
      };
    });

    return transformedData;
  };

  const processXmlFiles = (files, setLoading, setFiles) => {
    setLoading(true);
    const processedFiles = [];
    let completedFiles = 0;
    
    files.forEach(file => {
      const reader = new FileReader();
      
      reader.onload = (e) => {
        try {
          const xmlContent = e.target.result;
          processedFiles.push({
            name: file.name,
            content: xmlContent
          });
          
          completedFiles++;
          
          if (completedFiles === files.length) {
            setFiles(processedFiles);
            setXmlPreviewData(processedFiles.map(file => ({
              fileName: file.name,
              size: Math.round((new Blob([file.content]).size) / 1024) + ' KB'
            })));
            
            parseXmlPreview(processedFiles);
            
            setXmlDebugView(true);
            
            setLoading(false);
          }
        } catch (error) {
          console.error(`Error processing XML file ${file.name}:`, error);
          setMessage({ type: 'error', text: `Error processing file ${file.name}: ${error.message}` });
          setLoading(false);
        }
      };
      
      reader.readAsText(file);
    });
  };

  const parseXmlPreview = (xmlFiles) => {
    try {
      if (xmlFiles.length === 0) return;
      
      const parser = new DOMParser();
      const xmlDoc = parser.parseFromString(xmlFiles[0].content, "text/xml");
      
      const transactionDetail = xmlDoc.querySelector('TransactionDetail');
      if (!transactionDetail) {
        setMessage({ type: 'error', text: 'No se encontró la sección TransactionDetail en el XML' });
        return;
      }
      
      const header = transactionDetail.querySelector('WarehouseShippingAdviceHeader');
      if (!header) {
        setMessage({ type: 'error', text: 'No se encontró la sección WarehouseShippingAdviceHeader en el XML' });
        return;
      }
      
      const previewData = {
        Order_number: getElementText(header, 'ShipmentIdentification'),
        Customer_PO: getElementText(header, 'PurchaseOrderNumber'),
        customer: getElementText(header.querySelector('Address'), 'AddressName'),
        address: extractAddress(header.querySelector('Address')),
        City: getElementText(header.querySelector('Address'), 'City'),
        State: getElementText(header.querySelector('Address'), 'State'),
        Origin: extractOrigin(transactionDetail.querySelector('WarehouseShippingAdviceLine')),
        cuenta: "MIDEA MEXICO",
        Order_status: "CREATED",
        order_Substatus: "PENDIENTE",
        productos: extractProducts(transactionDetail.querySelectorAll('WarehouseShippingAdviceLine')),
      };
      
      setPreviewProcessedXmlData(previewData);
    } catch (error) {
      console.error('Error parsing XML preview:', error);
      setMessage({ type: 'error', text: 'Error al analizar el XML para la vista previa: ' + error.message });
    }
  };
  
  const getElementText = (parent, tagName) => {
    const element = parent?.querySelector(tagName);
    return element ? removeAccents(element.textContent.trim().toUpperCase()) : 'N/A';
  };
  
  const extractAddress = (addressElement) => {
    if (!addressElement) return 'N/A';
    
    const parts = [];
    for (let i = 1; i <= 4; i++) {
      const addr = addressElement.querySelector(`Address${i}`);
      if (addr && addr.textContent.trim()) {
        parts.push(removeAccents(addr.textContent.trim()));
      }
    }
    
    return parts.length > 0 ? parts.join(' ').toUpperCase() : 'N/A';
  };
  
  const extractOrigin = (lineElement) => {
    if (!lineElement) return 'N/A';
    
    const warehouseCode = lineElement.querySelector('WarehouseCode');
    if (warehouseCode && warehouseCode.textContent) {
      const codeText = warehouseCode.textContent.trim();
      
      // Extract prefix until the second space
      let spaceCount = 0;
      let prefixEndIndex = 0;
      
      for (let i = 0; i < codeText.length; i++) {
        if (codeText[i] === ' ') {
          spaceCount++;
          if (spaceCount === 2) {
            prefixEndIndex = i;
            break;
          }
        }
      }
      
      // If we found two spaces, use text up to the second space
      // Otherwise use the first part before any space
      let prefix;
      if (prefixEndIndex > 0) {
        prefix = removeAccents(codeText.substring(0, prefixEndIndex).trim().toUpperCase());
      } else {
        prefix = removeAccents(codeText.split(' ')[0].toUpperCase());
      }
      
      return prefix;
    }
    
    return 'N/A';
  };
  
  const extractProducts = (lineElements) => {
    const products = [];
    const productsByCode = {};
    
    lineElements.forEach(line => {
      const vendorPart = line.querySelector('VendorPartNumber');
      const qtyElement = line.querySelector('OrderQty');
      
      if (vendorPart && qtyElement && vendorPart.textContent && qtyElement.textContent) {
        // Apply both accent removal and special character sanitization
        const normalizedCode = removeAccents(vendorPart.textContent.trim().toUpperCase());
        const code = sanitizeProductCode(normalizedCode);
        
        const qty = parseInt(qtyElement.textContent);
        
        if (!isNaN(qty)) {
          if (productsByCode[code]) {
            productsByCode[code] += qty;
          } else {
            productsByCode[code] = qty;
          }
        }
      }
    });
    
    Object.entries(productsByCode).forEach(([code, qty]) => {
      products.push({
        codigo_producto: code,
        product_qt: qty
      });
    });
    
    return products;
  };

  const handleProcessXmlData = async () => {
    try {
      setMessage({ type: 'info', text: 'Procesando datos XML...' });
      
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('No se ha encontrado un token de autenticación. Por favor inicie sesión nuevamente.');
      }
      
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/v1/orders/process-xml`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ xmlFiles: xmlFiles }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Error al procesar los archivos XML');
      }

      const result = await response.json();
      setProcessedXmlData(result);
      setPreviewProcessedXmlData(null);
      setMessage({ type: 'success', text: `${result.length} órdenes validadas correctamente` });
    } catch (error) {
      console.error('Error en procesamiento XML:', error);
      setMessage({ type: 'error', text: error.message });
    }
  };

  const handleSaveXmlOrders = async () => {
    if (!processedXmlData) {
      setMessage({ type: 'error', text: 'No hay datos XML para guardar' });
      return;
    }

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('No se ha encontrado un token de autenticación. Por favor inicie sesión nuevamente.');
      }
      
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/v1/orders/create-from-xml`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ orders: processedXmlData }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Error al crear las órdenes desde XML');
      }

      const result = await response.json();
      
      setMessage({ 
        type: 'success', 
        text: `${result.length} órdenes XML creadas correctamente` 
      });

      setXmlFiles([]);
      setXmlPreviewData(null);
      setProcessedXmlData(null);
      setPreviewProcessedXmlData(null);
      setXmlDebugView(false);

      setTimeout(() => {
        setMessage({ type: '', text: '' });
      }, 3000);
    } catch (error) {
      setMessage({ 
        type: 'error', 
        text: error.message || 'Hubo un error al crear las órdenes desde XML' 
      });
    }
  };

  const processFile = (file, setLoading, setFile) => {
    setLoading(true);
    const reader = new FileReader();

    reader.onload = async (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: 'array' });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet);

        if (jsonData.length === 0) {
          throw new Error('El archivo está vacío');
        }

        setPreviewData(jsonData);
        setFile(file);
      } catch (error) {
        setMessage({ type: 'error', text: error.message });
      } finally {
        setLoading(false);
      }
    };

    reader.readAsArrayBuffer(file);
  };

  const handleProcessData = async () => {
    try {
      setMessage({ type: 'info', text: 'Validando datos...' });
      
      const transformedData = await transformData(previewData);
      setProcessedData(transformedData);
      setMessage({ type: 'success', text: 'Datos validados correctamente' });
    } catch (error) {
      console.error('Error en procesamiento:', error);
      setMessage({ type: 'error', text: error.message });
    }
  };

  const handleSaveChanges = async () => {
    if (!processedData) {
      setMessage({ type: 'error', text: 'No hay datos para guardar' });
      return;
    }

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('No se ha encontrado un token de autenticación. Por favor inicie sesión nuevamente.');
      }
      
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/v1/orders/bulk`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(processedData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Error al enviar los datos');
      }

      setMessage({ 
        type: 'success', 
        text: `${processedData.length} órdenes creadas correctamente` 
      });

      setFile1(null);
      setPreviewData(null);
      setProcessedData(null);

      setTimeout(() => {
        setMessage({ type: '', text: '' });
      }, 3000);

    } catch (error) {
      setMessage({ 
        type: 'error', 
        text: error.message || 'Hubo un error al crear las órdenes' 
      });
    }
  };

  const { getRootProps: getRootProps1, getInputProps: getInputProps1 } = useDropzone({
    accept: {
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'application/vnd.ms-excel': ['.xls']
    },
    multiple: false,
    onDrop: (acceptedFiles) => {
      processFile(acceptedFiles[0], setLoading1, setFile1);
    },
  });
  
  const { getRootProps: getRootPropsXml, getInputProps: getInputPropsXml } = useDropzone({
    accept: {
      'application/xml': ['.xml'],
      'text/xml': ['.xml']
    },
    multiple: true,
    onDrop: (acceptedFiles) => {
      processXmlFiles(acceptedFiles, setLoadingXml, setXmlFiles);
    },
  });

  const handleRemoveFile = (fileSetter) => {
    fileSetter(null);
    setProcessedData(null);
  };
  
  const handleRemoveXmlFiles = () => {
    setXmlFiles([]);
    setXmlPreviewData(null);
    setProcessedXmlData(null);
    setPreviewProcessedXmlData(null);
    setXmlDebugView(false);
  };

  const handleDownloadFile = (fileData, fileName) => {
    const ws = XLSX.utils.json_to_sheet(fileData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Sheet1");
    XLSX.writeFile(wb, fileName);
  };

  const templateData = [{
    Customer_PO: 'PO123',
    Order_number: 'DO123',
    customer: 'Cliente Ejemplo',
    City: 'Ciudad',
    State: 'Estado',
    address: 'Dirección',
    Appointment: '44197',
    Appointment_hr: '0.5',
    Origin: 'Origen',
    cuenta: 'Cuenta',
    codigo_producto: 'COD001',
    product_qt: 10
  }];

  return (
    <div className="flex">
      <Sidebar
        isOpen={sidebarOpen}
        setSidebarOpen={setSidebarOpen}
        activeItem={activeItem}
        setActiveItem={setActiveItem}
        onLogout={handleLogout}
      />
      <div
        className={`transition-all duration-300 ${sidebarOpen ? 'ml-64' : 'ml-16'} flex-1 p-6 bg-gray-100 min-h-screen`}
      >
        <Topbar 
          searchValue={searchValue}
          onSearchChange={setSearchValue}
        />
        
        <div className="mt-6 grid grid-cols-1 md:grid-cols-1 gap-6">
          <Section
            title="Carga Masiva de Órdenes"
            rootProps={getRootProps1}
            inputProps={getInputProps1}
            file={file1}
            className="border-2 border-dashed border-gray-400 rounded-lg p-8 flex justify-center items-center relative"
            loading={loading1}
            onRemove={() => {
              handleRemoveFile(setFile1);
              setPreviewData(null);
              setProcessedData(null);
            }}
          />
          
          {previewData && (
            <div className="bg-white p-4 rounded-lg shadow">
              <h3 className="text-lg font-semibold mb-4">Vista previa de datos</h3>
              <div className="overflow-x-auto">
                <table className="min-w-full table-auto">
                  <thead>
                    <tr className="bg-gray-100">
                      {Object.keys(previewData[0]).map((header) => (
                        <th key={header} className="px-4 py-2 text-left">{header}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {previewData.slice(0, 5).map((row, index) => (
                      <tr key={index} className="border-b">
                        {Object.values(row).map((value, i) => (
                          <td key={i} className="px-4 py-2">{String(value)}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
                {previewData.length > 5 && (
                  <p className="text-sm text-gray-500 mt-2">
                    Mostrando 5 de {previewData.length} registros
                  </p>
                )}
              </div>
              <div className="mt-4 flex justify-end space-x-2">
                <Button
                  onClick={() => {
                    handleRemoveFile(setFile1);
                    setPreviewData(null);
                  }}
                  className="bg-red-500 text-white"
                >
                  Cancelar
                </Button>
                <Button
                  onClick={handleProcessData}
                  className="bg-sky-700 text-white"
                  disabled={processedData !== null}
                >
                  Validar Datos
                </Button>
              </div>
            </div>
          )}
          
          <Button
            variant="outline"
            onClick={() => handleDownloadFile(templateData, 'Carga_Masiva_Ordenes.xlsx')}
            className="mt-2 p-2 bg-sky-700 text-white rounded-lg hover:bg-sky-800 flex items-center space-x-2"
          >
            <Download className="w-4 h-4" />
            <span>Descargar Plantilla</span>
          </Button>
          
          <Section
            title="Carga Masiva EDI (XML)"
            rootProps={getRootPropsXml}
            inputProps={getInputPropsXml}
            file={xmlFiles.length > 0 ? { name: `${xmlFiles.length} archivos seleccionados` } : null}
            className="border-2 border-dashed border-gray-400 rounded-lg p-8 flex justify-center items-center relative"
            loading={loadingXml}
            onRemove={() => {
              handleRemoveXmlFiles();
              setPreviewProcessedXmlData(null);
              setXmlDebugView(false);
            }}
          />
          
          {xmlPreviewData && xmlPreviewData.length > 0 && (
            <div className="bg-white p-4 rounded-lg shadow">
              <h3 className="text-lg font-semibold mb-4">Archivos XML cargados</h3>
              <div className="overflow-x-auto">
                <table className="min-w-full table-auto">
                  <thead>
                    <tr className="bg-gray-100">
                      <th className="px-4 py-2 text-left">Nombre del Archivo</th>
                      <th className="px-4 py-2 text-left">Tamaño</th>
                    </tr>
                  </thead>
                  <tbody>
                    {xmlPreviewData.map((file, index) => (
                      <tr key={index} className="border-b">
                        <td className="px-4 py-2">{file.fileName}</td>
                        <td className="px-4 py-2">{file.size}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="mt-4 flex justify-end space-x-2">
                <Button
                  onClick={() => setXmlDebugView(!xmlDebugView)}
                  className="bg-blue-500 text-white"
                >
                  {xmlDebugView ? 'Ocultar Debug' : 'Mostrar Debug'}
                </Button>
                <Button
                  onClick={handleRemoveXmlFiles}
                  className="bg-red-500 text-white"
                >
                  Cancelar
                </Button>
                <Button
                  onClick={handleProcessXmlData}
                  className="bg-sky-700 text-white"
                  disabled={processedXmlData !== null}
                >
                  Procesar XML
                </Button>
              </div>
            </div>
          )}

          {xmlDebugView && (previewProcessedXmlData || processedXmlData) && (
            <div className="bg-white p-4 rounded-lg shadow">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold">
                  {processedXmlData ? 'Datos Procesados (Listo para Guardar)' : 'Vista Previa de Interpretación XML'}
                </h3>
                <div className="flex space-x-2">
                  <Button 
                    onClick={() => setXmlDebugView(false)}
                    className="text-xs bg-gray-500 hover:bg-gray-600 text-white"
                    size="sm"
                  >
                    Ocultar
                  </Button>
                </div>
              </div>
              
              <div className="overflow-x-auto bg-gray-100 p-4 rounded">
                {processedXmlData ? (
                  <>
                    <h4 className="text-md font-medium mb-2">Información de Órdenes ({processedXmlData.length})</h4>
                    {processedXmlData.map((order, index) => (
                      <div key={index} className="mb-4 border border-gray-300 p-3 rounded bg-white">
                        <h5 className="font-bold">Orden #{index+1} - {order.Order_number}</h5>
                        <div className="grid grid-cols-2 gap-2 mt-2">
                          <div>
                            <p><span className="font-semibold">Customer_PO:</span> {order.Customer_PO || 'N/A'}</p>
                            <p><span className="font-semibold">Cliente:</span> {order.customer}</p>
                            <p><span className="font-semibold">Dirección:</span> {order.address || 'N/A'}</p>
                            <p><span className="font-semibold">Ciudad:</span> {order.City || 'N/A'}</p>
                            <p><span className="font-semibold">Estado:</span> {order.State || 'N/A'}</p>
                            <p><span className="font-semibold">Origen:</span> {order.Origin}</p>
                            <p><span className="font-semibold">Cuenta:</span> {order.cuenta}</p>
                          </div>
                          <div>
                            <p className="font-semibold">Productos:</p>
                            <ul className="list-disc pl-5">
                              {order.productos.map((product, pIndex) => (
                                <li key={pIndex}>
                                  ID: {product.product_id}, Cantidad: {product.product_qt}
                                </li>
                              ))}
                            </ul>
                          </div>
                        </div>
                      </div>
                    ))}
                    <div className="mt-4 flex justify-end">
                      <Button
                        onClick={handleSaveXmlOrders}
                        className="bg-green-600 hover:bg-green-700 text-white"
                      >
                        Confirmar y Guardar Órdenes
                      </Button>
                    </div>
                  </>
                ) : (
                  <>
                    <h4 className="text-md font-medium mb-2">Interpretación Preliminar del XML</h4>
                    <div className="mb-4 border border-gray-300 p-3 rounded bg-white">
                      <h5 className="font-bold text-amber-600">Vista Previa (sin validación de servidor)</h5>
                      <div className="grid grid-cols-2 gap-2 mt-2">
                        <div>
                          <p><span className="font-semibold">Order_number:</span> {previewProcessedXmlData.Order_number}</p>
                          <p><span className="font-semibold">Customer_PO:</span> {previewProcessedXmlData.Customer_PO || 'N/A'}</p>
                          <p><span className="font-semibold">Cliente:</span> {previewProcessedXmlData.customer}</p>
                          <p><span className="font-semibold">Dirección:</span> {previewProcessedXmlData.address || 'N/A'}</p>
                          <p><span className="font-semibold">Ciudad:</span> {previewProcessedXmlData.City || 'N/A'}</p>
                          <p><span className="font-semibold">Estado:</span> {previewProcessedXmlData.State || 'N/A'}</p>
                          <p><span className="font-semibold">Origen:</span> {previewProcessedXmlData.Origin}</p>
                          <p><span className="font-semibold">Cuenta:</span> {previewProcessedXmlData.cuenta}</p>
                          <p><span className="font-semibold">Estado:</span> {previewProcessedXmlData.Order_status}</p>
                          <p><span className="font-semibold">Subestado:</span> {previewProcessedXmlData.order_Substatus}</p>
                        </div>
                        <div>
                          <p className="font-semibold">Productos Encontrados:</p>
                          <ul className="list-disc pl-5">
                            {previewProcessedXmlData.productos.map((product, pIndex) => (
                              <li key={pIndex}>
                                <span className="font-medium">{product.codigo_producto}</span>: {product.product_qt} unidades
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    </div>
                    <div className="mt-4 flex justify-end">
                      <p className="text-sm italic text-gray-600 mr-4 self-center">
                        Esta es una vista previa. Haga clic en "Procesar XML" para la validación completa.
                      </p>
                      <Button
                        onClick={handleProcessXmlData}
                        className="bg-blue-600 hover:bg-blue-700 text-white"
                      >
                        Procesar XML
                      </Button>
                    </div>
                  </>
                )}
              </div>
            </div>
          )}

          {processedXmlData && !xmlDebugView && (
            <div className="fixed bottom-4 right-5 transform -translate-x-1/2 bg-sky-900 rounded-lg text-white">
              <Button onClick={handleSaveXmlOrders}>
                Guardar Órdenes XML
              </Button>
            </div>
          )}

          {processedData && (
            <div className="fixed bottom-4 right-5 transform -translate-x-1/2 bg-sky-900 rounded-lg text-white">
              <Button onClick={handleSaveChanges}>
                Guardar Cambios
              </Button>
            </div>
          )}
        </div>
        
        <div className="mt-4">
          {message.type === 'success' && (
            <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-2 rounded">
              {message.text}
            </div>
          )}
          {message.type === 'error' && (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-2 rounded">
              {message.text}
              <div className="text-sm mt-2">
                Por favor, verifique que los datos cumplen con el formato requerido.
              </div>
            </div>
          )}
          {message.type === 'info' && (
            <div className="bg-blue-100 border border-blue-400 text-blue-700 px-4 py-2 rounded">
              {message.text}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const Section = ({ title, rootProps, inputProps, description, file, className, loading, onRemove }) => {
  return (
    <div className="space-y-6">
      <h3 className="text-2xl font-semibold text-gray-900 text-center">{title}</h3>
      <div
        className={`${className} relative border-2 border-dashed border-gray-300 rounded-lg transition-all duration-300 hover:border-sky-500 hover:shadow-lg bg-white bg-opacity-40 p-8 shadow-lg`}
      >
        <div {...rootProps()} className="w-full h-full flex justify-center items-center cursor-pointer">
          <input {...inputProps()} />
          {loading ? (
            <div className="flex items-center justify-center text-gray-500 space-x-2">
              <Loader className="animate-spin text-sky-500 w-6 h-6" />
              <span className="text-lg font-semibold">Cargando...</span>
            </div>
          ) : file ? (
            <div className="flex items-center justify-between w-full">
              <div className="flex items-center space-x-3">
                <File className="text-gray-500" />
                <span className="text-sm font-medium text-gray-700 truncate">{file.name}</span>
              </div>
              <Button
                variant="link"
                onClick={(e) => {
                  e.stopPropagation(); // Prevent triggering dropzone click
                  onRemove();
                }}
                className="text-xs text-red-500 hover:text-red-700 font-semibold"
              >
                Eliminar
              </Button>
            </div>
          ) : (
            <div className="text-center">
              <p className="text-sm text-gray-500 mb-2">{description || 'Arrastra archivos aquí o haz clic para seleccionar'}</p>
              <div className="text-gray-600 font-semibold mt-2 flex flex-col items-center">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-gray-400 mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3-3m0 0l3 3m-3-3v12" />
                </svg>
                Arrastra aquí o haz clic para seleccionar
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MassMovements;